import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addMinutes, isSameDay } from 'date-fns'
import { db, FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
import { StockType } from 'types/stock'
import { DefaultStockType, ScheduleType } from 'types/schedule'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { dateToString, parseToDate } from 'lib/helper'
import { fetchStocks, initStocks, unsubscribeStocks } from 'reducks/stock/operations'
import { BreadType } from 'types/bread'
import { sendNewStoreMessage } from 'reducks/messages/operations'
import { updatePublishFirstStock } from 'reducks/store/operations'
import { State } from 'index'
import { getIsBeforePublishingStock, getStoreStatus } from 'reducks/store/selectors'
import { publishFirstStock } from 'repos/store'
import { closeModal } from 'reducks/modal/operations'
import { addNotifiedTypes } from 'reducks/bread/operations'
import { addStoreStockMessageQueue } from 'reducks/storeStockMessageQueue/operations'

const useSaveIndividualPreorderSchedule = (
  bread: BreadType,
  checkedOrderTypes: string[],
  date: Date | undefined,
  stockList: StockType[],
  storeRef: FirebaseDocRefType
): ((
  individualStocks: StockType[],
  defaultStocks: DefaultStockType[],
  hoursToDeadline: number,
  note: string,
  defaultSchedule: ScheduleType
) => void) => {
  const dispatch = useDispatch()
  const storeStatus = useSelector((state: State) => getStoreStatus(state))
  const isBeforePublishingStock = useSelector((state: State) => getIsBeforePublishingStock(state))
  return useCallback(
    async (
      individualStocks: StockType[],
      defaultStocks: DefaultStockType[],
      hoursToDeadline: number,
      note: string,
      defaultSchedule: ScheduleType
    ) => {
      if (!bread.bread_ref) return
      const batch = db.batch()
      const nextStockRefs: FirebaseDocRefType[] = []
      dispatch(showLoadingAction())
      // 日付が設定されていない異常系の処理
      if (typeof date === 'undefined') return

      for (let i = 0; i < individualStocks.length; i = (i + 1) | 0) {
        const individualStock = individualStocks[i]
        const defaultStock = defaultStocks[i]
        // 販売予定でない曜日の場合、stock_refを個別設定
        const stockId = `stock_preorder_${defaultSchedule.schedule_ref?.id}-${dateToString(date)}_${
          defaultStock.pickupable_time
        }`
        if (typeof individualStock.stock_ref === 'undefined') {
          // 新規作成
          individualStock.stock_ref = storeRef.collection('store_stocks').doc(stockId)
        } else if (typeof individualStock.stock_ref !== 'undefined' && stockId !== individualStock.stock_ref.id) {
          // 受渡解禁時刻の変更
          const deletedRef = individualStock.stock_ref
          batch.delete(deletedRef)
          individualStock.stock_ref = storeRef.collection('store_stocks').doc(stockId)
        }
        individualStock.pickupable_time = defaultStock.pickupable_time
        individualStock.stock = defaultStock.stock
        individualStock.deadline = FirebaseTimestamp.fromDate(
          addMinutes(parseToDate(individualStock.date), -(hoursToDeadline * 60))
        )
        individualStock.note = note
        if (!individualStock.stock_ref) continue
        nextStockRefs.push(individualStock.stock_ref)
        const ref = individualStock.stock_ref
        delete individualStock.stock_ref
        batch.set(ref, individualStock, { merge: true })
      }
      // 更新前のstockListに存在しているが更新対象のstockListに存在していない場合は削除
      const settingDateStocks = stockList.filter((stock) => isSameDay(date, parseToDate(stock.date)))
      for (let i = 0; i < settingDateStocks.length; i = (i + 1) | 0) {
        const stock = settingDateStocks[i]
        if (!nextStockRefs.some((nextStockRef) => stock.stock_ref?.id === nextStockRef.id)) {
          if (!stock.stock_ref) continue
          batch.delete(stock.stock_ref)
        }
      }
      // 店舗が初めて在庫を公開する場合、5km圏内のユーザーにPush通知を送る
      if (isBeforePublishingStock && storeStatus === 'public') {
        await publishFirstStock(storeRef)
        await dispatch(updatePublishFirstStock())
        await sendNewStoreMessage(storeRef)
      } else {
        const type = 'preorder'
        // この商品で初めて在庫を設定する場合、ユーザーに通知を送る
        if (!bread.notified_types?.includes(type)) {
          await Promise.all([
            addNotifiedTypes({ bread, type }),
            addStoreStockMessageQueue({ breadRef: bread.bread_ref, storeRef }),
          ])
        }
      }
      try {
        await batch.commit()
        await dispatch(unsubscribeStocks())
        await dispatch(initStocks())
        await dispatch(fetchStocks({ date: dateToString(date), types: checkedOrderTypes, storeRef }))
        dispatch(hideLoadingAction())
        dispatch(closeModal())
      } catch (e) {
        console.error(e)
        dispatch(hideLoadingAction())
      }
    },
    [bread, checkedOrderTypes, date, stockList, storeRef]
  )
}

export default useSaveIndividualPreorderSchedule
